import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PlaylistPlayOutlinedIcon from "@mui/icons-material/PlaylistPlayOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ToolTraceView from "../../components/ToolTraceView";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const MAX_POINTS = 900;
const CHART_CEILING = 0.09;
const MIN_ECHO_SAMPLES = 24;

const STATE_FILL = {
  escuchando: "rgba(74,108,247,.16)",
  hablando: "rgba(240,163,60,.20)",
  pensando: "rgba(140,150,170,.14)",
};

const STATE_COLOR = {
  escuchando: "#4a6cf7",
  hablando: "#f0a33c",
  pensando: "#8c96aa",
  idle: "#8c96aa",
  error: "#e05548",
};

const STATE_LABEL_KEY = {
  idle: "testing.avatar.states.idle",
  escuchando: "testing.avatar.states.listening",
  pensando: "testing.avatar.states.thinking",
  hablando: "testing.avatar.states.speaking",
  error: "testing.avatar.states.error",
};

const SENSITIVITY_INFO = {
  high: { labelKey: "testing.avatar.sensitivity.high", color: "#3fae74" },
  medium: { labelKey: "testing.avatar.sensitivity.medium", color: "#8c96aa" },
  low: { labelKey: "testing.avatar.sensitivity.low", color: "#d98b2b" },
};

const percentile = (values, p) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
};

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  help: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    maxWidth: 780,
    lineHeight: 1.55,
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  agentTree: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  agentTreeTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  agentRoot: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    border: "1px solid transparent",
    marginBottom: theme.spacing(0.75),
    transition: "background .25s ease, border-color .25s ease",
  },
  agentRootIdle: { background: theme.palette.action.hover },
  agentChildren: {
    marginLeft: 9,
    paddingLeft: theme.spacing(1.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  agentNode: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    border: "1px solid transparent",
    transition: "background .25s ease, border-color .25s ease",
  },
  agentActive: {
    background: "rgba(63,174,116,.14)",
    borderColor: "#3fae74",
  },
  agentSteadyDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#3fae74",
    flexShrink: 0,
  },
  agentLiveDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#3fae74",
    flexShrink: 0,
    animation: "$agentLivePulse 1.6s ease-in-out infinite",
  },
  "@keyframes agentLivePulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.35 },
  },
  agentNodeName: {
    fontSize: 12.5,
    fontWeight: 600,
  },
  agentNodeModel: {
    marginLeft: "auto",
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  agentVisitBadge: {
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  agentFlow: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px dashed ${theme.palette.divider}`,
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  callLayout: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr) minmax(340px, 420px)",
    gap: theme.spacing(2),
    alignItems: "stretch",
    height: "clamp(560px, calc(100vh - 270px), 760px)",
    [theme.breakpoints.down("xl")]: { gridTemplateColumns: "360px minmax(0, 1fr)" },
    [theme.breakpoints.down("lg")]: { gridTemplateColumns: "1fr", height: "auto" },
  },
  avatarColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minHeight: 0,
  },
  frame: {
    display: "block",
    width: "100%",
    flex: 1,
    minHeight: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    background: theme.palette.mode === "dark" ? "#0b0e14" : "#f2f3f6",
    [theme.breakpoints.down("lg")]: { flex: "none", height: 640 },
  },
  frameEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    borderStyle: "dashed",
    color: theme.palette.text.secondary,
  },
  measurements: {
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  chart: {
    display: "block",
    width: "100%",
    height: 190,
    borderRadius: 10,
    background: theme.palette.mode === "dark" ? "#0b0e14" : "#f4f6fa",
    border: `1px solid ${theme.palette.divider}`,
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  dot: {
    display: "inline-block",
    width: 9,
    height: 9,
    borderRadius: 3,
    marginRight: 6,
    verticalAlign: "middle",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  },
  metric: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  metricLabel: { fontSize: 11, color: theme.palette.text.secondary },
  metricValue: {
    fontSize: 20,
    fontVariantNumeric: "tabular-nums",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  metricNote: { fontSize: 11, color: theme.palette.text.disabled },
  verdict: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.55,
    border: "1px solid",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
  },
  rowTitle: { fontWeight: 500 },
  rowDetail: { fontSize: 12, color: theme.palette.text.secondary },
  empty: {
    padding: theme.spacing(3),
    borderRadius: 10,
    border: `1px dashed ${theme.palette.divider}`,
    fontSize: 13,
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  form: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  log: {
    height: "100%",
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: 12,
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("lg")]: { height: 420 },
  },
  logDim: { color: theme.palette.text.disabled },
  logSpeech: { margin: theme.spacing(0.25, 0) },
  logUser: { color: "#4a6cf7", fontWeight: 600 },
  logAvatar: { color: "#f0a33c", fontWeight: 600 },
  logKey: { color: theme.palette.text.secondary, fontWeight: 600 },
  logOk: { color: "#3fae74", fontWeight: 600 },
  logBad: { color: "#e05548" },
  logHint: { color: "#f0a33c" },
  logTool: {
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.divider}`,
  },
  logToolHead: { fontWeight: 600 },
  logToolHeadBad: { fontWeight: 600, color: "#e05548" },
  logStepHead: {
    paddingLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  logStepHeadBad: {
    paddingLeft: theme.spacing(2),
    color: "#e05548",
    fontWeight: 600,
  },
  logElapsed: {
    float: "right",
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.disabled,
    fontWeight: 400,
  },
  logLevel1: { paddingLeft: theme.spacing(2) },
  logLevel2: { paddingLeft: theme.spacing(4) },
  logRaw: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1),
    borderRadius: 6,
    maxHeight: 140,
    overflowY: "auto",
    fontSize: 11,
    lineHeight: 1.55,
    wordBreak: "break-all",
    background: theme.palette.mode === "dark" ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.03)",
    ...theme.scrollbarStyles,
  },
  transcript: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: 380,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  bubble: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    fontSize: 14,
    lineHeight: 1.5,
    maxWidth: "78%",
    whiteSpace: "pre-wrap",
  },
  bubbleCustomer: {
    alignSelf: "flex-end",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottomRightRadius: 4,
  },
  bubbleBot: {
    alignSelf: "flex-start",
    border: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: 4,
  },
  casesTable: {
    width: "100%",
    borderCollapse: "collapse",
    "& th": {
      textAlign: "left",
      padding: theme.spacing(1, 1.5),
      fontSize: "0.68rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: theme.palette.text.secondary,
      background: theme.palette.mode === "light" ? "#f7f6f3" : "#242320",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  caseRow: {
    borderTop: `1px solid ${theme.palette.divider}`,
    transition: "background .15s ease",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  caseRowFail: {
    background: "rgba(224,85,72,.08)",
    "&:hover": {
      background: "rgba(224,85,72,.14)",
    },
  },
  caseRowActive: {
    background: theme.palette.action.selected,
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    "&:hover": {
      background: theme.palette.action.selected,
    },
  },
  caseRowQueued: {
    opacity: 0.6,
  },
  filtersBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  searchField: {
    width: 320,
    maxWidth: "100%",
  },
  statusToggle: {
    "& .MuiToggleButton-root": {
      textTransform: "none",
      padding: theme.spacing(0.5, 1.5),
    },
  },
  groupRow: {
    cursor: "pointer",
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.mode === "light" ? "#f7f6f3" : "#242320",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  groupCell: {
    padding: theme.spacing(1, 1.5),
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  groupTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
  },
  groupSummary: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  groupFail: {
    fontSize: "0.8rem",
    fontWeight: 700,
    color: theme.palette.error.main,
  },
  caseIndent: {
    paddingLeft: theme.spacing(5),
  },
  formSection: {
    marginTop: theme.spacing(2),
  },
  formLabel: {
    fontWeight: 700,
    fontSize: "0.9rem",
    marginBottom: theme.spacing(0.5),
  },
  formHint: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  messageRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  messageNumber: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: 700,
    background: theme.palette.action.hover,
  },
  moreOptionsToggle: {
    textTransform: "none",
    marginTop: theme.spacing(1),
  },
  caseStatusLive: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12.5,
    lineHeight: 1.3,
  },
  caseStatusStep: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  caseCell: {
    padding: theme.spacing(1.25, 1.5),
    verticalAlign: "middle",
  },
  caseCellStatus: {
    width: 150,
    whiteSpace: "nowrap",
  },
  caseCellActions: {
    width: 150,
    whiteSpace: "nowrap",
  },
  caseDetailCell: {
    padding: theme.spacing(0, 1.5, 1.25),
  },
  scenarioTitle: { fontWeight: 600, fontSize: 14 },
  scenarioVerdictReason: {
    fontSize: 12.5,
    lineHeight: 1.4,
  },
  scenarioVerdictReasonPass: { color: "#2f8a5c" },
  scenarioVerdictReasonFail: { color: "#c2453c" },
  scenarioExpected: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.5,
  },
  scenarioExpectedClamped: {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  scenarioExpectedToggle: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.primary.main,
    cursor: "pointer",
    marginTop: theme.spacing(0.5),
    display: "inline-block",
    "&:hover": { textDecoration: "underline" },
  },
  scenarioActions: {
    marginLeft: "auto",
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  scenarioTranscript: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  scenarioTurn: {
    fontSize: 12.5,
    lineHeight: 1.6,
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    background: theme.palette.action.hover,
  },
  scenarioTurnBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    cursor: "default",
  },
  kpiBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0, 2),
  },
  kpiProgress: {
    flex: 1,
    minWidth: 120,
    height: 8,
    borderRadius: 4,
  },
  kpiProgressLabel: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  scenarioTraceLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  conversationDrawer: {
    width: "min(560px, 92vw)",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    maxHeight: "100vh",
    boxSizing: "border-box",
  },
  conversationTopSpacer: theme.mixins.toolbar,
  conversationHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  conversationHeaderTitle: {
    flex: 1,
    minWidth: 0,
  },
  conversationBody: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  conversationTurnLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
  },
  conversationBubbleRow: {
    display: "flex",
  },
  conversationBubbleRowEnd: {
    justifyContent: "flex-end",
  },
  conversationBubble: {
    maxWidth: "88%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    fontSize: 13.5,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  conversationBubbleCustomer: {
    background: theme.palette.action.hover,
    borderTopLeftRadius: 2,
  },
  conversationBubbleBot: {
    background: theme.palette.mode === "dark" ? "rgba(66,165,245,0.16)" : "rgba(33,150,243,0.08)",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(66,165,245,0.35)" : "rgba(33,150,243,0.22)"}`,
    color: theme.palette.text.primary,
    borderTopRightRadius: 2,
  },
  conversationBubbleLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  conversationBubbleLabelCustomer: {
    opacity: 0.65,
  },
  conversationBubbleLabelBot: {
    color: theme.palette.mode === "dark" ? "#82c4ff" : "#0d64b3",
  },
  conversationToolStep: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 6,
    "&::before, &::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
}));

const useConnections = (channel) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        const channels = Array.isArray(channel) ? channel : [channel];
        if (alive) setConnections((data || []).filter((c) => channels.includes(c.channel)));
      } catch (err) {
        toastError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [channel]);

  return { connections, loading };
};

const secondsLabel = (ms) => `${(Number(ms || 0) / 1000).toFixed(2)} s`;

const hintFor = ({ kind, status, error }) => {
  const texto = String(error || "");

  if (status === 401 || /api key/i.test(texto)) {
    return kind === "webhook" ? i18n.t("testing.hints.webhookAuth") : i18n.t("testing.hints.specialistApiKey");
  }

  if (status === 429 || /quota|rate limit/i.test(texto)) {
    return i18n.t("testing.hints.quota");
  }

  if (/^Timeout/i.test(texto)) {
    return i18n.t("testing.hints.timeout");
  }

  if (/URL bloqueada/i.test(texto)) {
    return i18n.t("testing.hints.blockedUrl");
  }

  if (status >= 500) {
    return i18n.t("testing.hints.serverError");
  }

  return null;
};

const ToolArgs = ({ args, indent, classes }) => {
  const entries = Object.entries(args || {});

  if (!entries.length) {
    return (
      <div className={indent}>
        <span className={classes.logDim}>{i18n.t("testing.trace.sends")} · </span>{i18n.t("testing.trace.noParams")}
      </div>
    );
  }

  return entries.map(([key, value], i) => (
    <div className={indent} key={key}>
      <span className={classes.logDim}>{i === 0 ? `${i18n.t("testing.trace.sends")} · ` : "        "}</span>
      <span className={classes.logKey}>{key}</span>{"  "}
      {typeof value === "string" ? value : JSON.stringify(value)}
    </div>
  ));
};

const ToolLines = ({ entry, indent, classes }) => (
  <>
    {entry.target && (
      <div className={indent}>
        {}
        <span className={classes.logDim}>{entry.kind === "webhook" ? `${i18n.t("testing.trace.goesTo")} · ` : `${i18n.t("testing.trace.model")} · `}</span>
        {entry.target}
        {typeof entry.status === "number" && (
          <span className={entry.status < 400 ? classes.logOk : classes.logBad}>
            {"  "}{entry.status}
          </span>
        )}
      </div>
    )}

    <ToolArgs args={entry.args} indent={indent} classes={classes} />

    {entry.error && (
      <>
        <div className={indent}>
          <span className={classes.logDim}>{i18n.t("testing.trace.error")} · </span>
          <span className={classes.logBad}>{entry.error}</span>
        </div>
        {hintFor(entry) && (
          <div className={indent}>
            <span className={classes.logDim}>{i18n.t("testing.trace.fix")} ·  </span>
            <span className={classes.logHint}>{hintFor(entry)}</span>
          </div>
        )}
      </>
    )}

    <div className={indent}>
      <span className={classes.logDim}>{i18n.t("testing.trace.returns")} · </span>
      {entry.result || i18n.t("testing.trace.noAnswer")}
    </div>

    {}
    {entry.raw && entry.raw !== entry.result && (
      <details className={indent}>
        <summary className={classes.logDim}>{i18n.t("testing.trace.raw", { count: entry.raw.length })}</summary>
        <div className={classes.logRaw}>{entry.raw}</div>
      </details>
    )}
  </>
);

const ToolEntry = ({ entry, classes }) => {
  const own = entry.trace.find((step) => step.name === entry.name);
  const nested = entry.trace.filter((step) => step !== own);
  const main = own ? { ...entry, ...own } : entry;

  return (
    <details className={classes.logTool}>
      <summary className={entry.ok ? classes.logToolHead : classes.logToolHeadBad}>
        {entry.time}{"  "}{i18n.t("testing.trace.tool")} {entry.name}
        <span className={classes.logElapsed}>{secondsLabel(entry.durationMs)}</span>
      </summary>

      <ToolLines entry={main} indent={classes.logLevel1} classes={classes} />

      {nested.map((step, i) => (
        <div key={i}>
          <div className={step.ok ? classes.logStepHead : classes.logStepHeadBad}>
            {"\u21b3 "}{step.kind === "webhook" ? i18n.t("testing.trace.webhook") : i18n.t("testing.trace.specialist")} {step.name}
            <span className={classes.logElapsed}>{secondsLabel(step.durationMs)}</span>
          </div>
          <ToolLines entry={step} indent={classes.logLevel2} classes={classes} />
        </div>
      ))}
    </details>
  );
};

const AvatarTest = ({ classes }) => {
  const { connections, loading } = useConnections("avatar");
  const [selectedId, setSelectedId] = useState("");
  const [inCall, setInCall] = useState(false);

  const canvasRef = useRef(null);
  const logRef = useRef(null);
  const historyRef = useRef([]);
  const thresholdRef = useRef(0.025);

  const [state, setState] = useState("idle");
  const [sensitivity, setSensitivity] = useState(null);
  const [metrics, setMetrics] = useState({
    level: 0, noise: 0, peak: 0, echo: 0, turns: 0, avgTurn: 0,
  });
  const [log, setLog] = useState([]);
  const [audioBlocked, setAudioBlocked] = useState(null);
  const [consultando, setConsultando] = useState(null);

  const connection = useMemo(
    () => connections.find((c) => String(c.id) === String(selectedId)),
    [connections, selectedId]
  );

  useEffect(() => {
    if (!selectedId && connections.length) setSelectedId(String(connections[0].id));
  }, [connections, selectedId]);

  const [promptInfo, setPromptInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    setPromptInfo(null);
    if (!connection?.promptId) return undefined;

    (async () => {
      try {
        const { data } = await api.get(`/prompt/${connection.promptId}`);
        if (alive) setPromptInfo(data);
      } catch {
      }
    })();

    return () => {
      alive = false;
    };
  }, [connection?.promptId]);

  const addLog = useCallback((text, tone, detail) => {
    const time = new Date().toLocaleTimeString("es", { hour12: false });
    setLog((p) =>
      [...p, { time, text, tone, id: Date.now() + Math.random(), ...detail }].slice(-200)
    );
  }, []);

  useEffect(() => {
    const box = logRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [log]);

  const recompute = useCallback(() => {
    const history = historyRef.current;
    if (!history.length) return;

    const noise = history.filter((h) => h.state === "escuchando" && !h.userSpeaking).map((h) => h.level);
    const voice = history.filter((h) => h.userSpeaking).map((h) => h.level);
    const echo = history.filter((h) => h.state === "hablando").map((h) => h.level);

    setMetrics((prev) => ({
      ...prev,
      level: history[history.length - 1].level,
      noise: percentile(noise, 0.5),
      peak: voice.length ? percentile(voice, 0.9) : 0,
      echo: echo.length >= MIN_ECHO_SAMPLES ? percentile(echo, 0.9) : 0,
    }));
  }, []);

  useEffect(() => {
    const onMessage = (ev) => {
      const d = ev.data;
      if (!d || d.source !== "avatar-expresito") return;

      if (d.type === "state") {
        setState(d.state);
        return;
      }

      if (d.type === "audioBlocked") {
        setAudioBlocked(d.reason || i18n.t("testing.avatar.unknown"));
        addLog(i18n.t("testing.avatar.audioBlockedLog", { reason: d.reason }), "bad");
        return;
      }

      if (d.type === "transcript") {
        addLog(d.text, undefined, { kind: "speech", who: d.who });
        return;
      }

      if (d.type === "toolStart") {
        setConsultando(d.name || null);
        return;
      }

      if (d.type === "tool") {
        setConsultando(null);
        addLog(null, d.ok ? undefined : "bad", {
          kind: "tool",
          name: d.name,
          args: d.args || {},
          durationMs: d.durationMs,
          ok: d.ok,
          result: d.result || "",
          trace: d.trace || [],
        });
        return;
      }

      if (d.type === "turn") {
        setMetrics((p) => ({
          ...p,
          turns: p.turns + 1,
          avgTurn: (p.avgTurn * p.turns + d.durationMs / 1000) / (p.turns + 1),
        }));
        addLog(i18n.t("testing.avatar.userTurnLog", { seconds: (d.durationMs / 1000).toFixed(1) }));
        return;
      }

      if (d.type === "level") {
        thresholdRef.current = d.threshold || thresholdRef.current;
        if (d.sensitivity) setSensitivity(d.sensitivity);
        const history = historyRef.current;
        history.push({
          level: d.level,
          state: d.state,
          armed: d.armed,
          userSpeaking: d.userSpeaking,
        });
        if (history.length > MAX_POINTS) history.shift();
        recompute();
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [addLog, recompute]);

  useEffect(() => {
    let raf = 0;

    const draw = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const dpr = window.devicePixelRatio || 1;
        const w = (canvas.width = canvas.clientWidth * dpr);
        const h = (canvas.height = canvas.clientHeight * dpr);
        const ctx = canvas.getContext("2d");
        const history = historyRef.current;
        const y = (v) => h - Math.min(v / CHART_CEILING, 1) * h;
        const step = w / MAX_POINTS;

        ctx.clearRect(0, 0, w, h);

        let from = 0;
        for (let i = 1; i <= history.length; i += 1) {
          const changed = i === history.length || history[i].state !== history[from].state;
          if (!changed) continue;
          const fill = STATE_FILL[history[from].state];
          if (fill) {
            ctx.fillStyle = fill;
            ctx.fillRect(from * step, 0, (i - from) * step, h);
          }
          from = i;
        }

        ctx.strokeStyle = "#f0a33c";
        ctx.lineWidth = dpr;
        ctx.beginPath();
        ctx.moveTo(0, y(thresholdRef.current));
        ctx.lineTo(w, y(thresholdRef.current));
        ctx.stroke();

        ctx.strokeStyle = "#3fae74";
        ctx.lineWidth = 1.5 * dpr;
        ctx.beginPath();
        history.forEach((p, i) => (i ? ctx.lineTo(i * step, y(p.level)) : ctx.moveTo(0, y(p.level))));
        ctx.stroke();

        ctx.fillStyle = "rgba(128,140,165,.85)";
        ctx.font = `${11 * dpr}px ui-monospace, Consolas, monospace`;
        [0.02, 0.04, 0.06, 0.08].forEach((v) => ctx.fillText(v.toFixed(2), 6 * dpr, y(v) - 4));
      }
      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  const start = () => {
    historyRef.current = [];
    setLog([]);
    setMetrics({ level: 0, noise: 0, peak: 0, echo: 0, turns: 0, avgTurn: 0 });
    setAudioBlocked(null);
    setConsultando(null);
    setSensitivity(null);
    setInCall(true);
  };

  const stop = () => {
    setInCall(false);
    setState("idle");
  };

  const margin = metrics.peak && metrics.noise ? metrics.peak / metrics.noise : 0;
  const echoRatio = metrics.echo / (thresholdRef.current || 0.025);

  const childSlug = (nombre) =>
    "consultar_" +
    String(nombre || "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const hijos = promptInfo?.promptsHijos || [];
  const slugToHijo = new Map(hijos.map((h) => [childSlug(h.name), h]));

  const visitas = log
    .filter((e) => e.kind === "tool")
    .map((e) => slugToHijo.get(String(e.name || "").replace(/_\d+$/, "")))
    .filter(Boolean);

  const visitasPorHijo = new Map();
  visitas.forEach((h) => visitasPorHijo.set(h.id, (visitasPorHijo.get(h.id) || 0) + 1));

  const hijoConsultando = consultando
    ? slugToHijo.get(String(consultando).replace(/_\d+$/, ""))
    : null;

  const verdict = (() => {
    if (!metrics.echo) return null;
    if (echoRatio >= 1) {
      return {
        color: "#e05548",
        text: i18n.t("testing.avatar.echo.high"),
      };
    }
    if (echoRatio >= 0.6) {
      return {
        color: "#d98b2b",
        text: i18n.t("testing.avatar.echo.edge"),
      };
    }
    return {
      color: "#3fae74",
      text: i18n.t("testing.avatar.echo.none"),
    };
  })();

  if (loading) return <Typography className={classes.help}>{i18n.t("testing.common.loadingConnections")}</Typography>;

  if (!connections.length) {
    return <Box className={classes.empty}>{i18n.t("testing.avatar.noConnections")}</Box>;
  }

  const readyToTest = connection?.avatarConfig?.enabled && connection?.promptId;

  return (
    <>
      <Box className={classes.callLayout}>
        <Box className={classes.avatarColumn}>
          <Box className={classes.toolbar}>
            <TextField
              select
              size="small"
              label={i18n.t("testing.avatar.connection")}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              slotProps={{ select: { native: true } }}
              disabled={inCall}
              style={{ minWidth: 160, flex: 1 }}
            >
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </TextField>

            {inCall ? (
              <Button
                variant="contained"
                color="error"
                startIcon={<StopCircleOutlinedIcon />}
                onClick={stop}
              >
                {i18n.t("testing.avatar.stop")}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<PlayArrowOutlinedIcon />}
                onClick={start}
                disabled={!readyToTest}
              >
                {i18n.t("testing.avatar.start")}
              </Button>
            )}

            {inCall && (
              <Chip
                size="small"
                label={STATE_LABEL_KEY[state] ? i18n.t(STATE_LABEL_KEY[state]) : state}
                variant="outlined"
                style={{ borderColor: STATE_COLOR[state], color: STATE_COLOR[state] }}
              />
            )}

            {inCall && sensitivity && (
              <Chip
                size="small"
                label={i18n.t("testing.avatar.thresholdChip", {
                  value: SENSITIVITY_INFO[sensitivity] ? i18n.t(SENSITIVITY_INFO[sensitivity].labelKey) : sensitivity,
                })}
                variant="outlined"
                style={{
                  borderColor: SENSITIVITY_INFO[sensitivity]?.color || STATE_COLOR.idle,
                  color: SENSITIVITY_INFO[sensitivity]?.color || STATE_COLOR.idle,
                }}
              />
            )}
          </Box>

          {connection && !readyToTest && (
            <Typography className={classes.rowDetail}>
              {!connection.avatarConfig?.enabled
                ? i18n.t("testing.avatar.disabled")
                : i18n.t("testing.common.noPrompt")}
            </Typography>
          )}

          {inCall && connection ? (
            <iframe
              title={i18n.t("testing.avatar.frameTitle")}
              className={classes.frame}
              src={`${BACKEND_URL}/avatar/${connection.token}/probar?diagnostico=1`}
              allow="microphone; autoplay"
            />
          ) : (
            <Box className={`${classes.frame} ${classes.frameEmpty}`}>
              <PlayArrowOutlinedIcon style={{ fontSize: 34, opacity: 0.35 }} />
              <Typography style={{ fontSize: 13, maxWidth: 260, lineHeight: 1.6 }}>
                {i18n.t("testing.avatar.startHint")}
              </Typography>
            </Box>
          )}

          {verdict && (
            <Box
              className={classes.verdict}
              style={{ borderColor: verdict.color, color: verdict.color }}
            >
              {verdict.text}
            </Box>
          )}
        </Box>

        <Box className={classes.measurements}>
          <canvas ref={canvasRef} className={classes.chart} />

          <Box className={classes.legend}>
            {[
              ["escuchando", i18n.t("testing.avatar.legend.listening")],
              ["hablando", i18n.t("testing.avatar.legend.speaking")],
              ["pensando", i18n.t("testing.avatar.legend.thinking")],
            ].map(([key, text]) => (
              <span key={key}>
                <i className={classes.dot} style={{ background: STATE_COLOR[key] }} />
                {text}
              </span>
            ))}
            <span>
              <i className={classes.dot} style={{ background: "#f0a33c" }} />
              {i18n.t("testing.avatar.legend.threshold")}
            </span>
          </Box>

          <Box className={classes.metrics}>
            {[
              [i18n.t("testing.avatar.metrics.noise"), metrics.noise.toFixed(4), i18n.t("testing.avatar.metrics.noiseNote")],
              [i18n.t("testing.avatar.metrics.peak"), metrics.peak ? metrics.peak.toFixed(4) : "—", i18n.t("testing.avatar.metrics.peakNote")],
              [
                i18n.t("testing.avatar.metrics.echo"),
                metrics.echo ? metrics.echo.toFixed(4) : "—",
                metrics.echo
                  ? i18n.t("testing.avatar.metrics.echoNote", { percent: Math.round(echoRatio * 100) })
                  : i18n.t("testing.avatar.metrics.echoEmpty"),
              ],
              [i18n.t("testing.avatar.metrics.margin"), margin ? `${margin.toFixed(1)}x` : "—", i18n.t("testing.avatar.metrics.marginNote")],
              [i18n.t("testing.avatar.metrics.turns"), metrics.turns, i18n.t("testing.avatar.metrics.turnsNote")],
              [
                i18n.t("testing.avatar.metrics.avgTurn"),
                metrics.avgTurn ? `${metrics.avgTurn.toFixed(1)} s` : "—",
                i18n.t("testing.avatar.metrics.avgTurnNote"),
              ],
            ].map(([label, value, note]) => (
              <Box className={classes.metric} key={label}>
                <Typography className={classes.metricLabel}>{label}</Typography>
                <Typography className={classes.metricValue}>{value}</Typography>
                <Typography className={classes.metricNote}>{note}</Typography>
              </Box>
            ))}
          </Box>

          {audioBlocked && (
            <Box className={classes.verdict} style={{ borderColor: "#e05548", color: "#e05548" }}>
              {i18n.t("testing.avatar.audioBlocked", { reason: audioBlocked })}
            </Box>
          )}

          {promptInfo && (
            <Box className={classes.agentTree}>
              <Typography className={classes.agentTreeTitle}>{i18n.t("testing.avatar.agentTree")}</Typography>

              <Box
                className={`${classes.agentRoot} ${
                  hijoConsultando ? classes.agentRootIdle : classes.agentActive
                }`}
              >
                {!hijoConsultando && <i className={classes.agentSteadyDot} />}
                <span className={classes.agentNodeName}>{promptInfo.name}</span>
                <span className={classes.agentNodeModel}>
                  {promptInfo.realtimeModel || "gpt-realtime-2.1-mini"}
                </span>
              </Box>

              <Box className={classes.agentChildren}>
                {hijos.map((hijo) => {
                  const visitasHijo = visitasPorHijo.get(hijo.id) || 0;
                  const consultandoEste = hijoConsultando?.id === hijo.id;

                  return (
                    <Box
                      key={hijo.id}
                      className={`${classes.agentNode} ${consultandoEste ? classes.agentActive : ""}`}
                    >
                      {consultandoEste && <i className={classes.agentLiveDot} />}
                      <span className={classes.agentNodeName}>{hijo.name}</span>
                      <span className={classes.agentNodeModel}>{hijo.model || "gpt-4o-mini"}</span>
                      {visitasHijo > 0 && (
                        <span className={classes.agentVisitBadge}>{visitasHijo}</span>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {visitas.length > 0 && (
                <Typography className={classes.agentFlow}>
                  {visitas.map((h) => h.name).join(" → ")}
                </Typography>
              )}
            </Box>
          )}

        </Box>

        <Box className={classes.log} ref={logRef}>
          {log.map((entry) => {
            if (entry.kind === "tool") {
              return <ToolEntry key={entry.id} entry={entry} classes={classes} />;
            }

            if (entry.kind === "speech") {
              return (
                <div key={entry.id} className={classes.logSpeech}>
                  <span className={classes.logDim}>{entry.time}{"  "}</span>
                  <span className={entry.who === "avatar" ? classes.logAvatar : classes.logUser}>
                    {entry.who === "avatar" ? i18n.t("testing.avatar.whoAvatar") : i18n.t("testing.avatar.whoYou")}
                  </span>
                  {"  "}{entry.text}
                </div>
              );
            }

            return (
              <div key={entry.id} className={entry.tone === "bad" ? classes.logBad : classes.logDim}>
                {entry.time}{"  "}{entry.text}
              </div>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

const STATUS_FILTERS = { all: "all", fail: "fail", pass: "pass", pending: "pending" };
const UNCATEGORIZED_KEY = "__uncategorized__";

const scenarioStatus = (result) =>
  result?.verdict === "pass" ? STATUS_FILTERS.pass : result?.verdict === "fail" ? STATUS_FILTERS.fail : STATUS_FILTERS.pending;

const resultForChannel = (lastResult, channel) => {
  if (!lastResult) return null;
  if (lastResult.byChannel) return lastResult.byChannel[channel] || null;
  return (lastResult.channel || "chat") === channel ? lastResult : null;
};

const formatDebug = (scenario, turns, result) => {
  const parts = [
    i18n.t("testing.debug.case", { title: scenario.title }),
    scenario.expected ? i18n.t("testing.debug.expected", { expected: scenario.expected }) : null,
  ];

  if (result?.verdict) {
    parts.push(
      i18n.t(result.auto ? "testing.debug.verdictAuto" : "testing.debug.verdict", {
        verdict: i18n.t(result.verdict === "pass" ? "testing.scenarios.passed" : "testing.scenarios.failed"),
      })
    );
    if (result.reason) parts.push(i18n.t("testing.debug.reason", { reason: result.reason }));
  }

  turns.forEach((turn, i) => {
    parts.push(
      "",
      i18n.t("testing.debug.turn", { number: i + 1 }),
      i18n.t("testing.debug.customer", { text: turn.customer }),
      i18n.t("testing.debug.ai", { text: turn.bot })
    );

    if (turn.trace?.length) {
      parts.push("", i18n.t("testing.debug.toolsCalled"));
      turn.trace.forEach((t, j) => {
        parts.push(
          `${j + 1}. [${t.kind}] ${t.name} (${t.target}) -- ${t.durationMs}ms -- ${t.ok ? "OK" : "ERROR"}`,
          `   Args: ${JSON.stringify(t.args)}`,
          `   ${i18n.t("testing.debug.result")}: ${t.result}`
        );
        if (t.error) parts.push(`   Error: ${t.error}`);
      });
    } else {
      parts.push("", i18n.t("testing.debug.noTools"));
    }
  });

  parts.push("", i18n.t("testing.debug.question"));

  return parts.filter((p) => p !== null).join("\n");
};

const TestScenarios = ({ classes, channel }) => {
  const selectedIsCall = channel === "call";
  const { connections, loading } = useConnections(selectedIsCall ? "sip" : "sandbox");
  const [selectedId, setSelectedId] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [results, setResults] = useState({});
  const [runningAll, setRunningAll] = useState(false);
  const [runQueue, setRunQueue] = useState({ position: 0, total: 0, title: "" });
  const stopRequestedRef = useRef(false);
  const [viewingScenarioId, setViewingScenarioId] = useState(null);
  const [expectedExpanded, setExpectedExpanded] = useState(false);

  const openConversation = (id) => {
    setViewingScenarioId(id);
    setExpectedExpanded(false);
  };
  const closeConversation = () => setViewingScenarioId(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", category: "", messages: [""], expected: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.all);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (!selectedId && connections.length) setSelectedId(String(connections[0].id));
  }, [connections, selectedId]);

  const fetchScenarios = useCallback(async () => {
    setLoadingScenarios(true);
    try {
      const { data } = await api.get("/test-scenarios");
      setScenarios(data);

      const preloaded = {};
      data.forEach((s) => {
        const last = resultForChannel(s.lastResult, channel);
        if (!last) return;
        preloaded[s.id] = {
          running: false,
          evaluating: false,
          turns: last.turns || [],
          verdict: last.verdict ?? null,
          auto: Boolean(last.auto),
          reason: last.reason || "",
        };
      });
      setResults(preloaded);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingScenarios(false);
    }
  }, [channel]);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const runScenario = async (scenario) => {
    if (!selectedId) return;

    const totalSteps = (scenario.messages || []).length;

    setResults((p) => ({
      ...p,
      [scenario.id]: { running: true, evaluating: false, queued: false, turns: [], verdict: null, auto: false, reason: "", step: 1, totalSteps },
    }));

    document.getElementById(`case-row-${channel}-${scenario.id}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });

    const numero = String(scenario.phone || "").trim() || String(9000000000 + Number(scenario.id));
    const turns = [];
    let ticketId = null;
    let callEnded = null;

    for (const mensaje of scenario.messages || []) {
      if (callEnded) {
        turns.push({
          customer: mensaje,
          bot: i18n.t(
            callEnded === "transferred"
              ? selectedIsCall
                ? "testing.scenarios.callTransferred"
                : "testing.scenarios.chatTransferred"
              : "testing.scenarios.callHungUp"
          ),
          trace: [],
        });
        continue;
      }
      try {
        if (selectedIsCall) {
          const { data } = await api.post(`/whatsapp/sandbox/${selectedId}/llamada`, {
            texto: mensaje,
            numero,
            historial: turns.map((t) => ({ customer: t.customer, bot: t.bot })),
          });
          callEnded = data.ended || null;
          turns.push({
            customer: mensaje,
            bot: data.botReply || i18n.t("testing.scenarios.aiSilent"),
            trace: data.trace || [],
          });
        } else {
          const { data } = await api.post(`/whatsapp/sandbox/${selectedId}/entrada`, {
            nombre: "Luigy",
            numero,
            texto: mensaje,
          });
          ticketId = data.ticketId ?? ticketId;
          callEnded = data.ended || null;
          turns.push({
            customer: mensaje,
            bot: data.botReply || i18n.t("testing.scenarios.noPromptReply"),
            trace: data.trace || [],
          });
        }
      } catch (err) {
        turns.push({
          customer: mensaje,
          bot: i18n.t("testing.scenarios.errorReply", { error: err?.response?.data?.error || err.message }),
          trace: [],
        });
      }
      setResults((p) => ({
        ...p,
        [scenario.id]: { ...p[scenario.id], running: true, turns: [...turns], step: Math.min(turns.length + 1, totalSteps) },
      }));
    }

    setResults((p) => ({
      ...p,
      [scenario.id]: { ...p[scenario.id], running: false, turns, verdict: null },
    }));

    let verdictFinal = null;
    let autoFinal = false;
    let reasonFinal = "";

    if (scenario.expected) {
      setResults((p) => ({ ...p, [scenario.id]: { ...p[scenario.id], evaluating: true } }));

      try {
        const { data } = await api.post(`/test-scenarios/${scenario.id}/evaluate`, {
          whatsappId: selectedId,
          transcript: turns,
        });
        verdictFinal = data.pass ? "pass" : "fail";
        autoFinal = true;
        reasonFinal = data.reason || "";
        setResults((p) => ({
          ...p,
          [scenario.id]: { ...p[scenario.id], evaluating: false, verdict: verdictFinal, auto: autoFinal, reason: reasonFinal },
        }));
      } catch (err) {
        reasonFinal = i18n.t("testing.scenarios.evalFailed");
        setResults((p) => ({
          ...p,
          [scenario.id]: { ...p[scenario.id], evaluating: false, reason: reasonFinal },
        }));
      }
    }

    try {
      await api.put(`/test-scenarios/${scenario.id}/result`, {
        verdict: verdictFinal,
        auto: autoFinal,
        reason: reasonFinal,
        turns,
        channel: selectedIsCall ? "call" : "chat",
      });
    } catch (err) {
    }

    if (ticketId) {
      try {
        await api.delete(`/tickets/${ticketId}`);
      } catch {
      }
    }
  };

  const runList = async (list) => {
    if (!selectedId || runningAll || !list.length) return;
    stopRequestedRef.current = false;
    setRunningAll(true);
    setResults((p) => ({ ...p, ...Object.fromEntries(list.map((scenario) => [scenario.id, { queued: true, turns: [] }])) }));

    for (let index = 0; index < list.length; index += 1) {
      if (stopRequestedRef.current) break;
      const scenario = list[index];
      setRunQueue({ position: index + 1, total: list.length, title: scenario.title });
      // eslint-disable-next-line no-await-in-loop
      await runScenario(scenario);
    }

    setResults((p) =>
      Object.fromEntries(Object.entries(p).filter(([, result]) => !result?.queued))
    );
    setRunQueue({ position: 0, total: 0, title: "" });
    setRunningAll(false);
  };

  const stopAll = () => {
    stopRequestedRef.current = true;
  };

  const openNew = () => {
    setEditing({});
    setForm({ title: "", category: "", messages: [""], expected: "", phone: "" });
    setShowMoreOptions(false);
    setDialogOpen(true);
  };

  const openEdit = (scenario) => {
    setEditing(scenario);
    setForm({
      title: scenario.title,
      category: scenario.category || "",
      messages: scenario.messages?.length ? [...scenario.messages] : [""],
      expected: scenario.expected || "",
      phone: scenario.phone || "",
    });
    setShowMoreOptions(Boolean(scenario.phone));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const saveDialog = async () => {
    const messages = form.messages.map((m) => m.trim()).filter(Boolean);

    if (!form.title.trim() || !messages.length) return;

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category.trim(),
        messages,
        expected: form.expected.trim(),
        phone: form.phone.trim(),
      };

      if (editing?.id) {
        await api.put(`/test-scenarios/${editing.id}`, payload);
      } else {
        await api.post("/test-scenarios", payload);
      }

      closeDialog();
      fetchScenarios();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const removeScenario = async (scenario) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(i18n.t("testing.scenarios.confirmDelete", { title: scenario.title }))) return;

    try {
      await api.delete(`/test-scenarios/${scenario.id}`);
      fetchScenarios();
    } catch (err) {
      toastError(err);
    }
  };

  const renderRow = (scenario) => {
    const result = results[scenario.id];
    const inProgress = Boolean(result?.running || result?.evaluating);
    const rowState = inProgress
      ? classes.caseRowActive
      : result?.queued
        ? classes.caseRowQueued
        : result?.verdict === "fail"
          ? classes.caseRowFail
          : "";
    const hasDetail = Boolean(result || scenario.expected);
    return (
      <React.Fragment key={scenario.id}>
        <tr
          id={`case-row-${channel}-${scenario.id}`}
          className={`${classes.caseRow} ${rowState}`}
          style={hasDetail ? { cursor: "pointer" } : undefined}
          onClick={hasDetail ? () => openConversation(scenario.id) : undefined}
        >
          <td className={`${classes.caseCell} ${classes.caseIndent}`}>
            <Box style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {hasDetail && (
                <Tooltip title={i18n.t("testing.scenarios.viewConversation")}>
                  <VisibilityOutlinedIcon fontSize="small" style={{ opacity: 0.6 }} />
                </Tooltip>
              )}
              <Typography className={classes.scenarioTitle}>{scenario.title}</Typography>
            </Box>
          </td>
          <td className={`${classes.caseCell} ${classes.caseCellStatus}`}>
            {result?.queued ? (
              <span className={classes.caseStatusLive} style={{ opacity: 0.8 }}>
                <HourglassEmptyOutlinedIcon style={{ fontSize: 15 }} />
                {i18n.t("testing.scenarios.queued")}
              </span>
            ) : result?.running ? (
              <span className={classes.caseStatusLive}>
                <CircularProgress size={14} thickness={5} />
                <span>
                  {i18n.t(channel === "call" ? "testing.scenarios.calling" : "testing.scenarios.chatting")}
                  {result.totalSteps > 0 && (
                    <span className={classes.caseStatusStep}>
                      {" · "}
                      {i18n.t("testing.scenarios.step", { current: result.step, total: result.totalSteps })}
                    </span>
                  )}
                </span>
              </span>
            ) : result?.evaluating ? (
              <span className={classes.caseStatusLive}>
                <CircularProgress size={14} thickness={5} color="secondary" />
                {i18n.t("testing.scenarios.evaluating")}
              </span>
            ) : result?.verdict ? (
              <Chip
                size="small"
                icon={result.verdict === "pass" ? <CheckCircleOutlinedIcon /> : <CancelOutlinedIcon />}
                label={i18n.t(result.verdict === "pass" ? "testing.scenarios.passed" : "testing.scenarios.failed")}
                color={result.verdict === "pass" ? "success" : "error"}
              />
            ) : null}
          </td>
          <td
            className={`${classes.caseCell} ${classes.caseCellActions}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Box className={classes.scenarioActions} style={{ marginLeft: 0, justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrowOutlinedIcon />}
                onClick={() => runScenario(scenario)}
                disabled={!selectedId || runningAll || inProgress}
              >
                {i18n.t("testing.scenarios.run")}
              </Button>
              <Tooltip title={i18n.t("testing.common.edit")}>
                <IconButton size="small" onClick={() => openEdit(scenario)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n.t("testing.common.delete")}>
                <IconButton size="small" onClick={() => removeScenario(scenario)}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {result && (
                <Tooltip title={i18n.t("testing.scenarios.copyDebug")}>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(formatDebug(scenario, result.turns, result));
                        toast.success(i18n.t("testing.scenarios.debugCopied"));
                      } catch {
                        toastError(new Error(i18n.t("testing.scenarios.copyFailed")));
                      }
                    }}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  const totalCount = scenarios.length;
  const passCount = Object.values(results).filter((r) => r?.verdict === "pass").length;
  const failCount = Object.values(results).filter((r) => r?.verdict === "fail").length;
  const doneCount = passCount + failCount;

  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(scenarios.map((scenario) => (scenario.category || "").trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [scenarios]
  );

  const statusCounts = useMemo(() => {
    const counts = { all: scenarios.length, fail: 0, pass: 0, pending: 0 };
    scenarios.forEach((scenario) => {
      counts[scenarioStatus(results[scenario.id])] += 1;
    });
    return counts;
  }, [scenarios, results]);

  const visibleScenarios = useMemo(() => {
    const term = search.trim().toLowerCase();
    return scenarios.filter((scenario) => {
      if (statusFilter !== STATUS_FILTERS.all && scenarioStatus(results[scenario.id]) !== statusFilter) return false;
      if (!term) return true;
      const haystack = [scenario.title, scenario.category, scenario.expected, ...(scenario.messages || [])]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [scenarios, results, search, statusFilter]);

  const groups = useMemo(() => {
    const byKey = new Map();
    visibleScenarios.forEach((scenario) => {
      const key = (scenario.category || "").trim();
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(scenario);
    });
    return Array.from(byKey.entries())
      .map(([key, items]) => {
        const all = scenarios.filter((scenario) => (scenario.category || "").trim() === key);
        return {
          key: key || UNCATEGORIZED_KEY,
          label: key || i18n.t("testing.groups.uncategorized"),
          items,
          total: all.length,
          passed: all.filter((scenario) => scenarioStatus(results[scenario.id]) === STATUS_FILTERS.pass).length,
          failed: all.filter((scenario) => scenarioStatus(results[scenario.id]) === STATUS_FILTERS.fail).length,
          running: items.some((scenario) => results[scenario.id]?.running || results[scenario.id]?.evaluating),
        };
      })
      .sort(
        (a, b) => (a.key === UNCATEGORIZED_KEY) - (b.key === UNCATEGORIZED_KEY) || a.label.localeCompare(b.label)
      );
  }, [visibleScenarios, scenarios, results]);

  const filtering = Boolean(search.trim()) || statusFilter !== STATUS_FILTERS.all;

  const isGroupExpanded = (group) => filtering || group.running || (expandedGroups[group.key] ?? group.failed > 0);

  const toggleGroup = (group) => setExpandedGroups((prev) => ({ ...prev, [group.key]: !isGroupExpanded(group) }));

  const updateMessage = (index, value) =>
    setForm((f) => ({ ...f, messages: f.messages.map((m, i) => (i === index ? value : m)) }));

  const addMessage = () => setForm((f) => ({ ...f, messages: [...f.messages, ""] }));

  const removeMessage = (index) =>
    setForm((f) => ({ ...f, messages: f.messages.length > 1 ? f.messages.filter((_, i) => i !== index) : [""] }));

  if (loading || loadingScenarios) {
    return <Typography className={classes.help}>{i18n.t("testing.common.loading")}</Typography>;
  }

  if (!connections.length) {
    return (
      <Box className={classes.empty}>
        {i18n.t(selectedIsCall ? "testing.scenarios.noCallConnections" : "testing.scenarios.noTextConnections")}
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.form}>
        <TextField
          select
          size="small"
          label={i18n.t("testing.scenarios.connection")}
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          slotProps={{ select: { native: true } }}
          style={{ minWidth: 220 }}
        >
          {connections.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </TextField>

        {runningAll ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopCircleOutlinedIcon />}
            onClick={stopAll}
          >
            {i18n.t("testing.scenarios.stopAll")}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<PlaylistPlayOutlinedIcon />}
            onClick={() => runList(visibleScenarios)}
            disabled={!visibleScenarios.length || !selectedId}
          >
            {filtering
              ? i18n.t("testing.groups.runFiltered", { count: visibleScenarios.length })
              : i18n.t("testing.scenarios.runAll")}
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<AddOutlinedIcon />}
          onClick={openNew}
          style={{ marginLeft: "auto" }}
        >
          {i18n.t("testing.scenarios.newCase")}
        </Button>
      </Box>

      {Boolean(totalCount) && (
        <Box className={classes.kpiBar}>
          <Typography className={classes.kpiProgressLabel}>
            {runningAll && runQueue.total
              ? i18n.t("testing.scenarios.runProgress", {
                  position: runQueue.position,
                  total: runQueue.total,
                  title: runQueue.title,
                })
              : `${doneCount}/${totalCount}`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={totalCount ? (doneCount / totalCount) * 100 : 0}
            color={failCount > 0 ? "error" : "success"}
            className={classes.kpiProgress}
          />
          <Chip
            size="small"
            icon={<CheckCircleOutlinedIcon />}
            label={i18n.t("testing.scenarios.passCount", { count: passCount })}
            color="success"
            variant={passCount ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            icon={<CancelOutlinedIcon />}
            label={i18n.t("testing.scenarios.failCount", { count: failCount })}
            color="error"
            variant={failCount ? "filled" : "outlined"}
          />
        </Box>
      )}

      {!scenarios.length && (
        <Box className={classes.empty}>{i18n.t("testing.scenarios.empty")}</Box>
      )}

      {scenarios.length > 0 && (
        <Box className={classes.filtersBar}>
          <TextField
            size="small"
            className={classes.searchField}
            placeholder={i18n.t("testing.filters.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: { startAdornment: <SearchOutlinedIcon fontSize="small" style={{ marginRight: 6, opacity: 0.6 }} /> },
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            className={classes.statusToggle}
            value={statusFilter}
            onChange={(e, value) => value && setStatusFilter(value)}
          >
            {Object.values(STATUS_FILTERS).map((value) => (
              <ToggleButton key={value} value={value}>
                {i18n.t(`testing.filters.${value}`, { count: statusCounts[value] })}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {scenarios.length > 0 && !visibleScenarios.length && (
        <Box className={classes.empty}>{i18n.t("testing.filters.noResults")}</Box>
      )}

      {visibleScenarios.length > 0 && (
        <Box style={{ overflowX: "auto" }}>
          <table className={classes.casesTable}>
            <thead>
              <tr>
                <th>{i18n.t("testing.scenarios.columns.case")}</th>
                <th className={classes.caseCellStatus}>{i18n.t("testing.scenarios.columns.status")}</th>
                <th className={classes.caseCellActions}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const expanded = isGroupExpanded(group);
                return (
                  <React.Fragment key={group.key}>
                    <tr className={classes.groupRow} onClick={() => toggleGroup(group)}>
                      <td colSpan={2} className={classes.groupCell}>
                        <Box className={classes.groupHeader}>
                          {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                          <Typography className={classes.groupTitle}>{group.label}</Typography>
                          <Typography className={classes.groupSummary}>
                            {i18n.t("testing.groups.summary", { passed: group.passed, total: group.total })}
                          </Typography>
                          {group.failed > 0 && (
                            <Typography className={classes.groupFail}>
                              {i18n.t("testing.groups.failedCount", { count: group.failed })}
                            </Typography>
                          )}
                        </Box>
                      </td>
                      <td className={`${classes.groupCell} ${classes.caseCellActions}`} onClick={(e) => e.stopPropagation()}>
                        <Box className={classes.scenarioActions} style={{ marginLeft: 0, justifyContent: "flex-end" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlaylistPlayOutlinedIcon />}
                            onClick={() => runList(group.items)}
                            disabled={!selectedId || runningAll}
                          >
                            {i18n.t("testing.groups.run", { count: group.items.length })}
                          </Button>
                        </Box>
                      </td>
                    </tr>
                    {expanded && group.items.map(renderRow)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={Boolean(viewingScenarioId)}
        onClose={closeConversation}
        slotProps={{ paper: { style: { height: "100vh", maxHeight: "100vh", borderRadius: 0 } } }}
      >
        {viewingScenarioId && (() => {
          const scenario = scenarios.find((s) => s.id === viewingScenarioId);
          const result = results[viewingScenarioId];
          if (!scenario) return null;
          return (
            <Box className={classes.conversationDrawer}>
              <Box className={classes.conversationTopSpacer} />
              <Box className={classes.conversationHeader}>
                <Box className={classes.conversationHeaderTitle}>
                  <Typography className={classes.scenarioTitle}>{scenario.title}</Typography>
                  {scenario.expected && (
                    <>
                      <Typography
                        className={`${classes.scenarioExpected} ${!expectedExpanded ? classes.scenarioExpectedClamped : ""}`}
                      >
                        <b>{i18n.t("testing.scenarios.expectedLabel")}</b> {scenario.expected}
                      </Typography>
                      {scenario.expected.length > 150 && (
                        <Typography
                          component="span"
                          className={classes.scenarioExpectedToggle}
                          onClick={() => setExpectedExpanded((v) => !v)}
                        >
                          {expectedExpanded ? i18n.t("testing.scenarios.showLess") : i18n.t("testing.scenarios.showMore")}
                        </Typography>
                      )}
                    </>
                  )}
                  {result?.verdict && (
                    <Box style={{ marginTop: 8 }}>
                      <Chip
                        size="small"
                        icon={result.verdict === "pass" ? <CheckCircleOutlinedIcon /> : <CancelOutlinedIcon />}
                        label={
                          i18n.t(result.verdict === "pass" ? "testing.scenarios.passed" : "testing.scenarios.failed") +
                          (result.auto ? ` ${i18n.t("testing.scenarios.automatic")}` : "")
                        }
                        color={result.verdict === "pass" ? "success" : "error"}
                      />
                      {result.reason && (
                        <Typography className={classes.scenarioVerdictReason} style={{ marginTop: 6 }}>
                          {result.reason}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <IconButton size="small" onClick={closeConversation}>
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box className={classes.conversationBody}>
                {!result?.turns?.length && (
                  <Typography className={classes.help}>{i18n.t("testing.scenarios.notRunYet")}</Typography>
                )}
                {result?.turns.map((t, i) => (
                  <Box key={i} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <Typography className={classes.conversationTurnLabel}>{i18n.t("testing.scenarios.turn", { number: i + 1 })}</Typography>

                    <Box className={classes.conversationBubbleRow}>
                      <Box className={`${classes.conversationBubble} ${classes.conversationBubbleCustomer}`}>
                        <div className={`${classes.conversationBubbleLabel} ${classes.conversationBubbleLabelCustomer}`}>
                          <PersonOutlineOutlinedIcon style={{ fontSize: 12 }} />
                          {i18n.t("testing.scenarios.customer")}
                        </div>
                        {t.customer}
                      </Box>
                    </Box>

                    {t.trace?.length > 0 && (
                      <>
                        <Typography className={classes.conversationToolStep}>
                          {i18n.t(t.trace.length === 1 ? "testing.scenarios.ranTool" : "testing.scenarios.ranTools")}
                        </Typography>
                        <ToolTraceView toolTrace={t.trace} />
                      </>
                    )}

                    <Box className={`${classes.conversationBubbleRow} ${classes.conversationBubbleRowEnd}`}>
                      <Box className={`${classes.conversationBubble} ${classes.conversationBubbleBot}`}>
                        <div className={`${classes.conversationBubbleLabel} ${classes.conversationBubbleLabelBot}`}>
                          <SmartToyOutlinedIcon style={{ fontSize: 12 }} />
                          {i18n.t("testing.scenarios.aiReply")}
                        </div>
                        {t.bot}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? i18n.t("testing.form.editTitle") : i18n.t("testing.form.newTitle")}</DialogTitle>
        <DialogContent>
          <Box style={{ display: "flex", gap: 12 }}>
            <TextField
              label={i18n.t("testing.form.title")}
              placeholder={i18n.t("testing.form.titlePlaceholder")}
              fullWidth
              margin="dense"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <Autocomplete
              freeSolo
              options={categoryOptions}
              inputValue={form.category}
              onInputChange={(e, value) => setForm((f) => ({ ...f, category: value }))}
              style={{ minWidth: 200, flexShrink: 0 }}
              renderInput={(params) => <TextField {...params} label={i18n.t("testing.form.category")} margin="dense" />}
            />
          </Box>

          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>{i18n.t("testing.form.customerWrites")}</Typography>
            <Typography className={classes.formHint}>{i18n.t("testing.form.customerWritesHint")}</Typography>
            {form.messages.map((message, index) => (
              <Box key={index} className={classes.messageRow}>
                <span className={classes.messageNumber}>{index + 1}</span>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder={i18n.t(
                    index === 0 ? "testing.form.firstMessagePlaceholder" : "testing.form.nextMessagePlaceholder"
                  )}
                  value={message}
                  onChange={(e) => updateMessage(index, e.target.value)}
                />
                <Tooltip title={i18n.t("testing.form.removeMessage")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => removeMessage(index)}
                      disabled={form.messages.length === 1 && !message}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
            <Button size="small" startIcon={<AddOutlinedIcon />} onClick={addMessage}>
              {i18n.t("testing.form.addMessage")}
            </Button>
          </Box>

          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>{i18n.t("testing.form.aiShould")}</Typography>
            <Typography className={classes.formHint}>{i18n.t("testing.form.aiShouldHint")}</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder={i18n.t("testing.form.aiShouldPlaceholder")}
              value={form.expected}
              onChange={(e) => setForm((f) => ({ ...f, expected: e.target.value }))}
            />
          </Box>

          <Button
            size="small"
            className={classes.moreOptionsToggle}
            startIcon={showMoreOptions ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            onClick={() => setShowMoreOptions((v) => !v)}
          >
            {i18n.t("testing.form.moreOptions")}
          </Button>
          <Collapse in={showMoreOptions}>
            <TextField
              label={i18n.t("testing.form.phone")}
              fullWidth
              margin="dense"
              placeholder={i18n.t("testing.form.phonePlaceholder")}
              helperText={i18n.t(selectedIsCall ? "testing.form.phoneHelpCall" : "testing.form.phoneHelpText")}
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{i18n.t("testing.common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={saveDialog}
            disabled={saving || !form.title.trim() || !form.messages.some((m) => m.trim())}
          >
            {i18n.t("testing.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SECTIONS = [
  { key: "textCases", labelKey: "testing.tabs.textCases", Component: TestScenarios, props: { channel: "chat" } },
  { key: "callCases", labelKey: "testing.tabs.callCases", Component: TestScenarios, props: { channel: "call" } },
  { key: "avatar", labelKey: "testing.tabs.avatar", Component: AvatarTest, props: {} },
];

const Testing = () => {
  const { classes } = useStyles();
  const [section, setSection] = useState(SECTIONS[0].key);

  const { Component, props } = SECTIONS.find((s) => s.key === section);

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>{i18n.t("testing.title")}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs value={section} onChange={(_, v) => setSection(v)} variant="scrollable">
          {SECTIONS.map((s) => (
            <Tab key={s.key} value={s.key} label={i18n.t(s.labelKey)} />
          ))}
        </Tabs>
        <Divider style={{ marginBottom: 16 }} />

        <Component key={section} classes={classes} {...props} />
      </Paper>
    </MainContainer>
  );
};

export default Testing;
